import { useCallback, useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@orisini/ui/components/button'
import { Card, CardContent } from '@orisini/ui/components/card'
import { api, ApiError } from '@orisini/ui/lib/api'
import { cn } from '@orisini/ui/lib/utils'
import { accountsApi, type AccountDto } from '@/features/accounts/api'
import { slotsApi, type SlotPreview } from '@/features/slots/api'
import { type PostDto } from '@/features/posts/api'
import { labelPost } from '@/features/posts/label'

/**
 * Kalender bulan (baca-saja di fase ini).
 *
 * Sel menampilkan post terjadwal + "ghost" untuk slot yang masih kosong — user
 * melihat KAPASITAS, bukan hanya konten. Drag post antar slot menyusul.
 */
export default function CalendarPage() {
  const [accounts, setAccounts] = useState<AccountDto[]>([])
  const [accountId, setAccountId] = useState('')
  const [queue, setQueue] = useState<PostDto[]>([])
  const [posted, setPosted] = useState<PostDto[]>([])
  const [preview, setPreview] = useState<SlotPreview | null>(null)
  const [tz, setTz] = useState('Asia/Jakarta')
  const [monthStart, setMonthStart] = useState(() => {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), 1)
  })

  useEffect(() => {
    accountsApi
      .list()
      .then((res) => {
        setAccounts(res.accounts)
        if (res.accounts.length) setAccountId((prev) => prev || res.accounts[0].id)
      })
      .catch(() => undefined)
  }, [])

  const load = useCallback(async (accId: string) => {
    const [queueRes, postedRes, prev] = await Promise.all([
      api.get<{ queue: PostDto[] }>(`/accounts/${accId}/queue`),
      api.get<{ posts: PostDto[] }>(`/posts?accountId=${accId}&status=posted&limit=200`),
      slotsApi.preview(accId).catch(() => null),
    ])
    setQueue(queueRes.queue)
    setPosted(postedRes.posts)
    setPreview(prev)
    if (prev) setTz(prev.timezone)
  }, [])

  useEffect(() => {
    if (accountId) {
      void load(accountId).catch((e: unknown) =>
        toast.error(e instanceof ApiError ? e.message : "Couldn't load the calendar"),
      )
    }
  }, [accountId, load])

  /** Kunci hari lokal (YYYY-MM-DD di zona akun) untuk sebuah epoch ms. */
  const dayKey = useCallback(
    (ms: number) => {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(new Date(ms))
      return parts // en-CA menghasilkan YYYY-MM-DD
    },
    [tz],
  )

  const byDay = useMemo(() => {
    const map = new Map<string, { posts: PostDto[]; ghosts: number }>()
    const push = (key: string) => {
      let cell = map.get(key)
      if (!cell) {
        cell = { posts: [], ghosts: 0 }
        map.set(key, cell)
      }
      return cell
    }
    for (const p of [...queue, ...posted]) {
      const at = p.scheduledAt ?? p.postedAt
      if (!at) continue
      push(dayKey(at)).posts.push(p)
    }
    // Slot kosong = instan preview yang tidak ditempati post mana pun.
    const taken = new Set(queue.map((p) => p.scheduledAt).filter(Boolean) as number[])
    for (const inst of preview?.instants ?? []) {
      if (!taken.has(inst.at)) push(dayKey(inst.at)).ghosts++
    }
    return map
  }, [queue, posted, preview, dayKey])

  const weeks = useMemo(() => {
    const year = monthStart.getFullYear()
    const month = monthStart.getMonth()
    const first = new Date(year, month, 1)
    const startOffset = first.getDay() // 0=Minggu
    const daysInMonth = new Date(year, month + 1, 0).getDate()

    const cells: Array<{ key: string; day: number } | null> = []
    for (let i = 0; i < startOffset; i++) cells.push(null)
    for (let d = 1; d <= daysInMonth; d++) {
      const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
      cells.push({ key, day: d })
    }
    while (cells.length % 7 !== 0) cells.push(null)

    const rows: (typeof cells)[] = []
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7))
    return rows
  }, [monthStart])

  const todayKey = dayKey(Date.now())
  const monthLabel = monthStart.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Calendar</h1>
          <p className="text-sm text-muted-foreground">Zone {tz} · about ±2 minutes</p>
        </div>
        <div className="flex items-center gap-2">
          {accounts.length > 1 && (
            <select
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            >
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label}
                </option>
              ))}
            </select>
          )}
          <Button
            variant="outline"
            size="icon"
            onClick={() => setMonthStart(new Date(monthStart.getFullYear(), monthStart.getMonth() - 1, 1))}
          >
            <ChevronLeft />
          </Button>
          <span className="min-w-36 text-center text-sm font-medium">{monthLabel}</span>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setMonthStart(new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 1))}
          >
            <ChevronRight />
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-7 gap-px text-center text-xs font-medium text-muted-foreground">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
              <div key={d} className="pb-2">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-px overflow-hidden rounded-md border bg-border">
            {weeks.flat().map((cell, i) => {
              const data = cell ? byDay.get(cell.key) : undefined
              return (
                <div
                  key={i}
                  className={cn(
                    'min-h-24 space-y-1 bg-card p-1.5',
                    cell?.key === todayKey && 'bg-primary/5 ring-1 ring-inset ring-primary/40',
                  )}
                >
                  {cell && (
                    <>
                      <p className="text-xs text-muted-foreground">{cell.day}</p>
                      {data?.posts.slice(0, 3).map((p) => (
                        <div
                          key={p.id}
                          className={cn(
                            'truncate rounded px-1 py-0.5 text-[10px] leading-tight',
                            p.status === 'posted'
                              ? 'bg-emerald-500/15 text-emerald-400'
                              : p.postMode === 'fixed'
                                ? 'bg-primary/20 text-primary'
                                : 'bg-indigo-500/15 text-indigo-300',
                          )}
                          title={labelPost(p) ?? undefined}
                        >
                          {new Date((p.scheduledAt ?? p.postedAt)!).toLocaleTimeString('en-GB', {
                            timeZone: tz,
                            hour: '2-digit',
                            minute: '2-digit',
                          })}{' '}
                          {labelPost(p) || '(post)'}
                        </div>
                      ))}
                      {data && data.posts.length > 3 && (
                        <p className="text-[10px] text-muted-foreground">+{data.posts.length - 3} more</p>
                      )}
                      {data && data.ghosts > 0 && (
                        <div className="rounded border border-dashed border-muted-foreground/30 px-1 py-0.5 text-center text-[10px] text-muted-foreground">
                          {data.ghosts} empty slots
                        </div>
                      )}
                    </>
                  )}
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
